import { differenceInYears } from "date-fns";
import { WithDefinedProperties } from "./types";

/**
 * Check if the given argument is undefined or null
 *
 * @param value {T | undefined | null} value which we want to verify the presence of
 * @returns {boolean} returns whether or not the value is defined
 */
export function isDefined<T>(value: T | undefined | null): value is T {
  return typeof value !== "undefined" && value !== null;
}

/**
 * Checks if a given value is undefined
 *
 * @param value
 * @returns {boolean} value is undefined
 */
export function isUndefined<T>(value: T | undefined): value is undefined {
  return typeof value === "undefined";
}

/**
 * Checks if a given value is null
 *
 * @param value
 * @returns {boolean} value is null
 */
export function isNull<T>(value: T | null): value is null {
  return value === null;
}

/**
 * Checks if an array, string or object is empty
 *
 * @param value
 * @returns {boolean} value is empty, returns true if undefined or null
 */
export function isEmpty(
  value: string | any[] | Record<string, unknown> | undefined | null,
): boolean {
  if (isUndefined(value) || isNull(value)) {
    return true;
  }

  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === 0;
  } else {
    return Object.keys(value).length === 0;
  }
}

/**
 * Checks if an array, string or object is empty
 *
 * @param value
 * @returns {boolean} value is not empty, returns false if undefined or null
 */
export function isNotEmpty<T extends string | any[] | Record<string, unknown>>(
  value: T | undefined | null,
): value is T {
  return !isEmpty(value);
}

/**
 * Check if the provided properties of the given record are defined
 *
 * @param object record which properties we want to check
 * @param properties properties of the record we want to check
 * @returns {boolean} returns whether or not the properties are defined on the record
 */
export function hasDefinedProperties<
  Key extends Property,
  TObject extends Record<Key, unknown>,
  Property extends keyof TObject,
>(
  object: TObject,
  properties: Property[],
): object is WithDefinedProperties<TObject, Property> {
  return !properties.some((property) => !isDefined(object[property]));
}

export const isOlderThan = (years: number, dateOfBirth: Date): boolean => {
  const today = new Date();

  return Math.abs(differenceInYears(dateOfBirth, today)) >= years;
};

export const isYoungerThan = (years: number, dateOfBirth: Date): boolean => {
  const age = Math.abs(differenceInYears(new Date(), dateOfBirth));
  return age - years < 0;
};

export const assertExhaustive = (type: never, error: Error): never => {
  throw error;
};
